import { useState, useEffect, useMemo } from 'react';
import { useHistory as useRouterHistory } from 'react-router-dom';
import { orderBy } from 'lodash';
import { toast } from 'react-toastify';

import { Table } from '../../components/Table';
import { Button } from '../../components/Button';
import { routesPath } from '../../constants/routes';
import { SearchBar } from '../../components/SearchBar';
import { RefreshIcon } from '../../components/RefreshIcon';
import { ColoredSlug } from '../../components/ColoredSlug';
import { useHistory, useDebouncedValue } from '../../hooks';
import { PermissionCheck } from '../../components/PermissionCheck';
import { useQueryAdministration } from '../../graphql/query/getAdministration';
import { formatToUserTimeZone, convertStringToDateTZ } from '../../utilities/dateTime';
import { useUserContext } from '../../context/userContext';
import { adminApi, KeyspaceInfo } from '../../api/admin';
import clsx from 'clsx';

type SortField =
  | 'orgName'
  | 'usersAmount'
  | 'roomsAmount'
  | 'surgeonsAmount'
  | 'casesAmount'
  | 'lastSyncAt';
type SortOrder = 'asc' | 'desc';

const filterHospitals = (hospitals?: any[], search?: string) => {
  if (!hospitals) return hospitals;
  if (!search) return hospitals;

  const processedSearch = search.toLocaleLowerCase().trim();
  return hospitals?.filter((h) => h.orgName.toLocaleLowerCase().trim().includes(processedSearch));
};

export const Administration = () => {
  const history = useHistory();
  const rawHistory = useRouterHistory();
  const { data: userData, onOrgChange, refetch } = useUserContext();
  const [isResyncDisabled, setIsResyncDisabled] = useState(false);
  const [cooldownSeconds, setCooldownSeconds] = useState(0);
  const [sortField, setSortField] = useState<SortField>('orgName');
  const [sortOrder, setSortOrder] = useState<SortOrder>('asc');
  const [isFlushingDb1, setIsFlushingDb1] = useState(false);
  const [isFlushingDb2, setIsFlushingDb2] = useState(false);
  const [cacheCountDb1, setCacheCountDb1] = useState<number | null>(null);
  const [cacheCountDb2, setCacheCountDb2] = useState<number | null>(null);
  const [keyspaceInfo, setKeyspaceInfo] = useState<KeyspaceInfo | null>(null);
  const [redisDbNum, setRedisDbNum] = useState<string>('1');
  const [isLoadingKeys, setIsLoadingKeys] = useState(false);

  const handleUpdateOrganization = (orgSlug: string, checked: boolean) => {
    updateOrganization(orgSlug, checked);
  };

  const handleStartAddNewHospital = () => {
    history.push(routesPath.newHospital.replace('/:refId', ''));
  };

  const handleGoToHospital = (refId?: string, name?: string) => {
    if (!refId) return;

    rawHistory.push(`/${refId}/dashboard`);
    onOrgChange(refId, name);
    refetch();
  };

  const { hospitals, loading, resync, updateOrganization } = useQueryAdministration();
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search);

  // Also if email is admin@example.com
  const isAuthorizedForCacheFlush =
    userData?.me?.email === 'ramtin.rajikermani@intusurg.com' ||
    userData?.me?.email === 'admin@example.com';

  const fetchCacheCounts = async () => {
    if (!isAuthorizedForCacheFlush) return;

    try {
      const [db1Response, db2Response, keyspaceResponse] = await Promise.all([
        adminApi.getCacheCountDb1(),
        adminApi.getCacheCountDb2(),
        adminApi.getKeyspaceInfo(),
      ]);
      setCacheCountDb1(db1Response.data.count);
      setCacheCountDb2(db2Response.data.count);
      setKeyspaceInfo(keyspaceResponse.data.keyspace);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to fetch cache counts:', error);
    }
  };

  useEffect(() => {
    fetchCacheCounts();
  }, [isAuthorizedForCacheFlush]);

  // Countdown timer effect
  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (cooldownSeconds > 0) {
      interval = setInterval(() => {
        setCooldownSeconds((prev) => {
          if (prev <= 1) {
            setIsResyncDisabled(false);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [cooldownSeconds]);

  const handleResync = () => {
    resync(undefined);
    setIsResyncDisabled(true);
    setCooldownSeconds(30);
  };

  const handleFlushDb1 = async () => {
    setIsFlushingDb1(true);
    try {
      const response = await adminApi.flushCacheDb1();
      toast.success(response.data.message, {
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
      });
      await fetchCacheCounts();
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to clear Celery cache', {
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
      });
    } finally {
      setIsFlushingDb1(false);
    }
  };

  const handleFlushDb2 = async () => {
    setIsFlushingDb2(true);
    try {
      const response = await adminApi.flushCacheDb2();
      toast.success(response.data.message, {
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
      });
      await fetchCacheCounts();
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to clear Flask cache', {
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
      });
    } finally {
      setIsFlushingDb2(false);
    }
  };

  const handleGetRedisKeys = async () => {
    const dbNum = parseInt(redisDbNum, 10);
    if (isNaN(dbNum) || dbNum < 0 || dbNum > 15) {
      toast.error('Please enter a valid database number (0-15)', {
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
      });
      return;
    }

    setIsLoadingKeys(true);
    try {
      const response = await adminApi.getRedisKeys(dbNum, 20);
      // eslint-disable-next-line no-console
      console.log(`Redis DB${dbNum} Keys (${response.data.count} items):`, response.data.keys);
      toast.success(
        `Retrieved ${response.data.count} keys from DB${dbNum}. Check console for details.`,
        {
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
        },
      );
    } catch (error: any) {
      // eslint-disable-next-line no-console
      console.error('Failed to get Redis keys:', error);
      toast.error(error.response?.data?.detail || 'Failed to get Redis keys', {
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
      });
    } finally {
      setIsLoadingKeys(false);
    }
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const SortableHeader = ({ field, label }: { field: SortField; label: string }) => (
    <div
      className="cursor-pointer select-none flex items-center gap-2"
      onClick={() => handleSort(field)}
    >
      {label}
      {sortField === field && <span className="text-xs">{sortOrder === 'asc' ? '▲' : '▼'}</span>}
    </div>
  );

  const filteredHospitals = useMemo(() => {
    const filtered = filterHospitals(hospitals, debouncedSearch);
    if (!filtered) return filtered;

    return orderBy(
      filtered,
      [
        (item) => {
          const value = item[sortField];
          // Handle null/undefined for lastSyncAt
          if (sortField === 'lastSyncAt' && !value) return '';
          // Convert to lowercase for case-insensitive string sorting
          if (typeof value === 'string') return value.toLowerCase();
          return value;
        },
      ],
      [sortOrder],
    );
  }, [hospitals, debouncedSearch, sortField, sortOrder]);

  return (
    <div className="pt-12">
      <div className="flex flex-row items-center justify-between mb-12">
        <div className="flex gap-x-8 w-1/2">
          <SearchBar placeholder="Search Hospitals..." value={search} onChange={setSearch} />
        </div>
        <div className="ml-4 flex gap-x-4">
          <PermissionCheck check="updateIndex">
            <Button type="light" onClick={handleResync} disabled={isResyncDisabled}>
              {isResyncDisabled
                ? `Resync all hospitals (${cooldownSeconds}s)`
                : 'Resync all hospitals'}
            </Button>
          </PermissionCheck>
          <PermissionCheck check="createOrganizations">
            <Button onClick={handleStartAddNewHospital} type="dark">
              Add New Hospital
            </Button>
          </PermissionCheck>
        </div>
      </div>
      <Table
        loading={loading}
        className="text-text-black"
        data={filteredHospitals ?? []}
        config={{
          columns: [
            {
              header: () => 'Sync ?',
              getValue: ({ orgSlug, orgActive }) => (
                <input
                  onChange={(e) => handleUpdateOrganization(orgSlug, e.target.checked)}
                  checked={orgActive}
                  className={clsx(
                    'checked:bg-persianBlue mb-1 cursor-pointer checked:border-persianBlue  w-[1.2rem] h-[1.2rem] ring-0 border-black bg-transparent',
                    { 'rounded-[.2rem]': true },
                  )}
                  type={'checkbox'}
                />
              ),
            },
            {
              header: () => <SortableHeader field="orgName" label="Hospitals" />,
              getValue: ({ orgName, orgRefId }) => (
                <div
                  className="cursor-pointer"
                  onClick={() => handleGoToHospital(orgRefId, orgName)}
                >
                  <ColoredSlug
                    name={orgName}
                    refId={orgRefId}
                    size="medium"
                    className="font-bold"
                  />
                </div>
              ),
            },
            {
              header: () => <SortableHeader field="usersAmount" label="Users" />,
              getValue: ({ usersAmount }) => usersAmount,
            },
            {
              header: () => <SortableHeader field="roomsAmount" label="Rooms" />,
              getValue: ({ roomsAmount }) => roomsAmount,
            },
            {
              header: () => <SortableHeader field="surgeonsAmount" label="Surgeons" />,
              getValue: ({ surgeonsAmount }) => surgeonsAmount,
            },
            {
              header: () => <SortableHeader field="casesAmount" label="Cases" />,
              getValue: ({ casesAmount }) => casesAmount,
            },
            {
              header: () => 'Resync',
              getValue: ({ orgSlug, currentSync }) =>
                currentSync === false ? (
                  <PermissionCheck check="updateIndex">
                    <Button type="light" onClick={() => resync(orgSlug)} icon={<RefreshIcon />} />
                  </PermissionCheck>
                ) : null,
            },
            {
              header: () => <SortableHeader field="lastSyncAt" label="Last Sync" />,
              getValue: ({ lastSyncAt }) =>
                lastSyncAt ? formatToUserTimeZone(convertStringToDateTZ(lastSyncAt)) : '',
            },
          ],
        }}
      />
      {isAuthorizedForCacheFlush && (
        <div className="mt-8 pt-8 border-t border-gray-300">
          <h3 className="text-lg font-semibold mb-4">Redis Cache Management</h3>
          <div className="flex gap-x-4">
            <Button type="light" onClick={handleFlushDb1} disabled={isFlushingDb1}>
              {isFlushingDb1
                ? 'Clearing...'
                : `Clear Celery Cache${cacheCountDb1 !== null ? ` (${cacheCountDb1} items)` : ''}`}
            </Button>
            <Button type="light" onClick={handleFlushDb2} disabled={isFlushingDb2}>
              {isFlushingDb2
                ? 'Clearing...'
                : `Clear Flask Cache${cacheCountDb2 !== null ? ` (${cacheCountDb2} items)` : ''}`}
            </Button>
          </div>
          <div className="mt-4 flex gap-x-4 items-center">
            <input
              type="text"
              value={redisDbNum}
              onChange={(e) => setRedisDbNum(e.target.value)}
              placeholder="DB number (0-15)"
              className="px-3 py-2 border border-gray-300 rounded w-40"
            />
            <Button type="light" onClick={handleGetRedisKeys} disabled={isLoadingKeys}>
              {isLoadingKeys ? 'Loading...' : 'Get Keys'}
            </Button>
          </div>
          {keyspaceInfo && (
            <div className="mt-6 p-4 bg-gray-50 border border-gray-300 rounded font-mono text-sm">
              <div className="font-semibold mb-2 text-gray-700"># Keyspace</div>
              {Object.entries(keyspaceInfo).map(([dbName, dbInfo]) => (
                <div key={dbName} className="text-gray-800">
                  {dbName}:keys={dbInfo.keys},expires={dbInfo.expires},avg_ttl={dbInfo.avg_ttl}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
